import { useEffect, useRef, useState } from 'react'
import StatsBand from '@/components/StatsBand/StatsBand'
import CtaSection from '@/components/CtaSection/CtaSection'
import styles from './AmbassadorPage.module.css'

// ── Journey steps data ──────────────────────────────────────────────────────
const JOURNEY_STEPS = [
  { icon: '📝', num: '01', label: 'Apply',       color: '#8b5cf6', glow: 'rgba(139,92,246,0.35)', desc: 'Fill the application form on PromptNexus after logging in to your account.' },
  { icon: '🔍', num: '02', label: 'Review',      color: '#6366f1', glow: 'rgba(99,102,241,0.35)',  desc: 'Our team reviews your profile, challenge history, and college eligibility within 14 days.' },
  { icon: '⚡', num: '03', label: 'Challenge',   color: '#a855f7', glow: 'rgba(168,85,247,0.35)', desc: 'Attempt the exclusive Selection Challenge designed specifically for your cohort.' },
  { icon: '🏆', num: '04', label: 'Selected',    color: '#eab308', glow: 'rgba(234,179,8,0.35)',   desc: 'Top performers per course per college are selected. One ambassador per stream.' },
  { icon: '🚀', num: '05', label: 'Onboard',     color: '#ec4899', glow: 'rgba(236,72,153,0.35)',  desc: 'Complete your ambassador profile and acknowledge the NexusAI Code of Conduct.' },
  { icon: '🎯', num: '06', label: 'Tasks & NAP', color: '#10b981', glow: 'rgba(16,185,129,0.35)',  desc: 'Complete monthly tasks, earn NAP points, and climb the ambassador leaderboard.' },
  { icon: '🎁', num: '07', label: 'Rewards',     color: '#f59e0b', glow: 'rgba(245,158,11,0.35)',  desc: 'Win cash prizes, internships, SkillNexus credits, and official recognition.' },
]

// ── Benefits ────────────────────────────────────────────────────────────────
const BENEFITS = [
  { icon: '⚡', color: '#8b5cf6', title: 'Earn NAP Points',       desc: 'Every challenge, PromptPath, LinkedIn post, and referral you complete earns NAP — your ambassador currency.' },
  { icon: '🏆', color: '#eab308', title: 'Win Real Rewards',       desc: 'Cash prizes, paid internship opportunities, SkillNexus credits, and exclusive swag every month.' },
  { icon: '📈', color: '#10b981', title: 'Build Your Profile',     desc: 'Verified LinkedIn credentials, NexusAI Ambassador certificate, and a public ambassador profile card.' },
  { icon: '🎓', color: '#6366f1', title: 'Lead Your Campus',       desc: 'Host workshops, grow NexusAI\'s presence at your college, and become the go-to AI person on campus.' },
  { icon: '🧠', color: '#ec4899', title: 'Stay Sharp',             desc: 'Get exclusive biweekly prompt engineering challenges to keep your AI skills on the cutting edge.' },
  { icon: '✅', color: '#f59e0b', title: 'Official Recognition',   desc: 'NexusAI Ambassador badge on your profile, digital credentials, and priority placement for internships.' },
]

// ── NAP activities ──────────────────────────────────────────────────────────
const NAP_ACTIVITIES = [
  { icon: '⚡', text: 'PromptNexus Challenge — Attempt the monthly challenge assigned to ambassadors' },
  { icon: '🗺️', text: 'PromptPath Completion — Finish the assigned PromptPath and submit your certificate' },
  { icon: '💬', text: 'Feedback Submission — Share feedback on challenges and PromptPaths after completing them' },
  { icon: '🔗', text: 'LinkedIn Share — Post your achievement on LinkedIn with the NexusAI tag' },
  { icon: '👥', text: 'Student Referrals — Refer 10 new students monthly to hit the milestone bonus' },
  { icon: '🎖️', text: 'Badge Card Share — Share your Ambassador badge card on LinkedIn at each level-up' },
]

// ── Requirements ────────────────────────────────────────────────────────────
const REQUIREMENTS = [
  { icon: '🎓', label: 'Enrolled Student',   desc: 'Currently enrolled in any UG/PG program at a recognised college or university' },
  { icon: '💡', label: 'AI Enthusiasm',      desc: 'Strong interest in AI, prompt engineering, or technology — no prior experience required' },
  { icon: '🌐', label: 'Active on LinkedIn', desc: 'Have an active LinkedIn profile to share achievements and build your professional network' },
  { icon: '⏱️', label: '5–8 hrs/month',      desc: 'Ability to commit 5–8 hours per month to tasks, challenges, and referral activities' },
]

// ── Animated journey component ────────────────────────────────────────────
function AnimatedJourney() {
  const [visible, setVisible] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const observer = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisible(true) },
      { threshold: 0.1 }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  return (
    <div ref={ref} className={styles.journeyWrap}>
      <style>{`
        @keyframes amb-step-up {
          from { opacity:0; transform:translateY(28px) scale(0.95); }
          to   { opacity:1; transform:translateY(0) scale(1); }
        }
        @keyframes amb-pulse-ring {
          0%   { transform: scale(1);   opacity: 0.6; }
          100% { transform: scale(2.2); opacity: 0; }
        }
        @keyframes amb-chev-flow {
          0%, 100% { opacity: 0.1; transform: rotate(45deg) translate(0,0); }
          50%       { opacity: 0.85; transform: rotate(45deg) translate(3px,-3px); }
        }
        .amb-step-web { opacity: 0; }
        .amb-step-web.vis { animation: amb-step-up 0.55s cubic-bezier(0.22,1,0.36,1) forwards; }
        .amb-arrow-wrap {
          display: flex; align-items: center; gap: 4px;
          flex-shrink: 0; margin-top: 33px; padding: 0 4px;
          opacity: 0; transition: opacity 0.9s ease;
        }
        .amb-arrow-wrap.vis { opacity: 1; }
        .amb-arrow-chev {
          display: block; width: 7px; height: 7px;
          border-right: 2px solid currentColor; border-top: 2px solid currentColor;
          border-radius: 1px;
          transform: rotate(45deg);
          animation: amb-chev-flow 1.5s ease-in-out infinite;
        }
      `}</style>

      <div className={styles.stepsRow}>
        {JOURNEY_STEPS.flatMap((s, i) => {
          const stepEl = (
            <div
              key={s.label}
              className={`amb-step-web ${styles.stepCol} ${visible ? 'vis' : ''}`}
              style={{ animationDelay: `${0.08 + i * 0.13}s` }}
            >
              <div
                className={styles.stepIcon}
                style={{
                  background: `${s.color}18`,
                  border: `1.5px solid ${s.color}55`,
                  boxShadow: `0 6px 28px ${s.glow}`,
                }}
              >
                <span className={styles.stepEmoji}>{s.icon}</span>
                <span className={styles.stepBadge} style={{ background: s.color }}>{s.num}</span>
                {i === 0 && visible && (
                  <span className={styles.pingRing} style={{ borderColor: s.color }} />
                )}
              </div>
              <p className={styles.stepLabel} style={{ color: s.color }}>{s.label}</p>
              <p className={styles.stepDesc}>{s.desc}</p>
            </div>
          )
          if (i === JOURNEY_STEPS.length - 1) return [stepEl]
          const arrowEl = (
            <div
              key={`arrow-${i}`}
              className={`amb-arrow-wrap ${visible ? 'vis' : ''}`}
              style={{ color: s.color, transitionDelay: `${0.15 + i * 0.13}s` }}
            >
              <span className="amb-arrow-chev" style={{ animationDelay: '0s' }} />
              <span className="amb-arrow-chev" style={{ animationDelay: '0.25s' }} />
              <span className="amb-arrow-chev" style={{ animationDelay: '0.5s' }} />
            </div>
          )
          return [stepEl, arrowEl]
        })}
      </div>

      {/* Mobile: vertical */}
      <div className={styles.stepsMobile}>
        {JOURNEY_STEPS.map((s, i) => (
          <div
            key={s.label}
            className={`amb-step-web ${styles.stepRow} ${visible ? 'vis' : ''}`}
            style={{ animationDelay: `${0.06 + i * 0.1}s` }}
          >
            <div className={styles.stepRowLeft}>
              <div
                className={styles.stepIconSm}
                style={{ background: `${s.color}18`, border: `1.5px solid ${s.color}45`, boxShadow: `0 2px 12px ${s.glow}` }}
              >
                {s.icon}
                <span className={styles.stepBadgeSm} style={{ background: s.color }}>{s.num}</span>
              </div>
              {i < JOURNEY_STEPS.length - 1 && (
                <div
                  className={styles.stepConnectorLine}
                  style={{ background: `linear-gradient(to bottom,${s.color}60,${JOURNEY_STEPS[i+1].color}30)` }}
                />
              )}
            </div>
            <div className={styles.stepRowText}>
              <p className={styles.stepLabelSm} style={{ color: s.color }}>{s.label}</p>
              <p className={styles.stepDescSm}>{s.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function AmbassadorPage() {
  return (
    <>
      {/* ── HERO ── */}
      <section className={styles.hero}>
        <div className={styles.heroBg} aria-hidden="true" />
        <div className={styles.heroContent}>
          <span className={styles.heroBadge}>
            <span className={styles.badgeDot} />
            Now Accepting Applications · Cohort 2025
          </span>
          <h1 className={styles.heroTitle}>
            Become a <span className="grad">NexusAI</span><br />
            Campus Ambassador
          </h1>
          <p className={styles.heroSub}>
            Lead AI adoption at your college. Earn NAP points, win real rewards,
            and build credentials that actually open doors.
          </p>
          <div className={styles.heroActions}>
            <a href="https://pn.nexusaiweb.com/ambassador" target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-lg">
              Apply Now
            </a>
            <a href="#journey" className="btn btn-ghost btn-lg">
              See How It Works
            </a>
          </div>
        </div>
      </section>

      {/* ── STATS BAND ── */}
      <StatsBand />

      {/* ── WHAT IS IT ── */}
      <section className={styles.whatSection}>
        <div className={styles.sectionInner}>
          <p className="section-label">About the Program</p>
          <h2 className="section-title">The NexusAI Ambassador Program</h2>
          <div className={styles.whatGrid}>
            <div className={styles.whatText}>
              <p className={styles.whatPara}>
                The NexusAI Ambassador Program (NAMP) is a structured, merit-based program that selects top students from every college to represent NexusAI on their campus.
              </p>
              <p className={styles.whatPara}>
                Ambassadors complete monthly tasks, earn <strong>NAP (NexusAI Ambassador Prompt Points)</strong>, and compete on a live leaderboard — with real rewards at every milestone: cash prizes, paid internships, SkillNexus credits, and verified professional credentials.
              </p>
              <p className={styles.whatPara}>
                Selection is based on merit — your challenge performance and PromptPath activity determine your rank. One ambassador per course per college.
              </p>
            </div>
            <div className={styles.whatStats}>
              {[
                { val: '1',     label: 'Ambassador per stream per college', color: '#8b5cf6' },
                { val: '7',     label: 'Journey stages from apply to rewards', color: '#eab308' },
                { val: '₹10K+', label: 'Monthly reward pool for top performers', color: '#10b981' },
                { val: '14',    label: 'Days max to get reviewed after applying', color: '#ec4899' },
              ].map(({ val, label, color }) => (
                <div key={label} className={styles.whatStat} style={{ borderColor: `${color}30` }}>
                  <p className={styles.statVal} style={{ color }}>{val}</p>
                  <p className={styles.statLabel}>{label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── JOURNEY STEPS ── */}
      <section id="journey" className={styles.journeySection}>
        <div className={styles.sectionInner}>
          <div className={styles.sectionHead}>
            <p className="section-label">Your Ambassador Journey</p>
            <h2 className="section-title">From application to rewards —<br />every step, fully transparent.</h2>
            <p className="section-desc" style={{ margin: '0 auto' }}>
              No guesswork. No black boxes. Here is exactly what happens after you hit Apply.
            </p>
          </div>
          <AnimatedJourney />
        </div>
      </section>

      {/* ── BENEFITS ── */}
      <section className={styles.benefitsSection}>
        <div className={styles.sectionInner}>
          <div className={styles.sectionHead}>
            <p className="section-label">Why Join</p>
            <h2 className="section-title">Built for students who want<br />to lead, learn, and earn.</h2>
          </div>
          <div className={styles.benefitsGrid}>
            {BENEFITS.map(({ icon, color, title, desc }) => (
              <div key={title} className={styles.benefitCard} style={{ borderColor: `${color}25` }}>
                <div className={styles.benefitIcon} style={{ background: `${color}18` }}>
                  <span style={{ fontSize: '20px' }}>{icon}</span>
                </div>
                <h3 className={styles.benefitTitle}>{title}</h3>
                <p className={styles.benefitDesc}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── NAP SYSTEM ── */}
      <section className={styles.napSection}>
        <div className={styles.sectionInner}>
          <div className={styles.napCard}>
            <div className={styles.napLeft}>
              <span style={{ fontSize: '40px', lineHeight: 1 }}>🎯</span>
              <div>
                <p className="section-label" style={{ marginBottom: '8px' }}>Scoring System</p>
                <h2 className={styles.napTitle}>How NAP Points Work</h2>
                <p className={styles.napDesc}>
                  NAP (NexusAI Ambassador Prompt Points) is your single scoring currency. Every approved activity earns NAP — all values are set by admin, so it's fully transparent and dynamic.
                </p>
              </div>
            </div>
            <ul className={styles.napList}>
              {NAP_ACTIVITIES.map(({ icon, text }) => (
                <li key={text} className={styles.napItem}>
                  <span className={styles.napItemIcon}>{icon}</span>
                  <span className={styles.napItemText}>{text}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ── REQUIREMENTS ── */}
      <section className={styles.reqSection}>
        <div className={styles.sectionInner}>
          <div className={styles.sectionHead}>
            <p className="section-label">Who Can Apply</p>
            <h2 className="section-title">Requirements</h2>
            <p className="section-desc" style={{ margin: '0 auto' }}>
              No prior experience with AI required — just enthusiasm and consistency.
            </p>
          </div>
          <div className={styles.reqGrid}>
            {REQUIREMENTS.map(({ icon, label, desc }) => (
              <div key={label} className={styles.reqCard}>
                <div className={styles.reqIcon}>{icon}</div>
                <h3 className={styles.reqLabel}>{label}</h3>
                <p className={styles.reqDesc}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <CtaSection
        label="Ready to Lead?"
        title={'Apply to become a\nNexusAI Ambassador'}
        sub="One spot per stream, per college. Apply early — review starts immediately."
        ctaText="Apply on PromptNexus"
        ctaHref="https://pn.nexusaiweb.com/ambassador"
      />
    </>
  )
}
